/**
 * Community Accounts Tab Component
 * Displays accounts associated with a community in a table format
 */

import { useEffect, useState, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Button } from '@/core/components/ui/button';
import { Label } from '@/core/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/core/components/ui/radio-group';
import { RefreshCw, AlertCircle, Pencil } from 'lucide-react';
import { AssignedAccountCheckbox } from '@/features/communities/components/atoms/AssignedAccountCheckbox';
import { AccountsSearchBar } from '@/features/communities/components/molecules/AccountsSearchBar';
import { AccountDateRangeDialog } from '@/features/communities/components/molecules/AccountDateRangeDialog';
import {
  useCommunityAccountsStore,
  filterAccounts,
} from '@/features/communities/stores/community-accounts-store';
import { useCommunitiesStore } from '@/features/communities/stores/communities-store';

interface CommunityAccountsTabProps {
  communityId: number;
}

export function CommunityAccountsTab({ communityId }: CommunityAccountsTabProps) {
  const { accounts, isLoading, error, loadAccounts, upsertAccountRelation, setFilters, filters, reset } =
    useCommunityAccountsStore();
  const { loadCommunities } = useCommunitiesStore();
  const filteredAccounts = filterAccounts(
    accounts || [],
    filters || { search: '', statusFilter: 'All', linkedFilter: 'Linked' }
  );

  // Ref for search input to focus on filter change
  const searchInputRef = useRef<HTMLInputElement>(null);

  // State for date range dialog
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<{
    id: number;
    name: string;
    startDate?: string;
    expirationDate?: string | null;
  } | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');

  // Load accounts on mount and when communityId changes
  useEffect(() => {
    loadAccounts(communityId);

    // Cleanup on unmount
    return () => {
      reset();
    };
  }, [communityId, loadAccounts, reset]);

  // Handle retry
  const handleRetry = () => {
    loadAccounts(communityId);
  };

  // Handle search change
  const handleSearchChange = (value: string) => {
    setFilters({ search: value });
  };

  // Handle filter change
  const handleFilterChange = (value: string) => {
    setFilters({ linkedFilter: value as 'Linked' | 'All' });

    // Focus search bar after filter change
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  // Handle checkbox change (assign/unassign account)
  const handleCheckboxChange = async (
    accountId: number,
    firstName: string,
    lastName: string,
    isLinked: boolean
  ) => {
    try {
      if (isLinked) {
        // Show date picker dialog
        setSelectedAccount({ id: accountId, name: `${firstName} ${lastName}` });
        setDialogMode('create');
        setDateDialogOpen(true);
      } else {
        // Unassign by setting expiration date to today (keep original startDate)
        const account = accounts.find((acc) => acc.accountId === accountId);
        const originalStartDate =
          account?.startDate || account?.associatedDate || format(subDays(new Date(), 1), 'yyyy-MM-dd');
        const today = format(new Date(), 'yyyy-MM-dd');
        await upsertAccountRelation(communityId, accountId, originalStartDate, today);

        // Refresh communities list to update the assigned accounts count
        await loadCommunities();
      }
    } catch (error) {
      console.error('Failed to update account assignment:', error);
    }
  };

  // Handle edit account button click
  const handleEditAccount = (
    accountId: number,
    firstName: string,
    lastName: string,
    startDate: string | undefined,
    expirationDate: string | null | undefined
  ) => {
    const formattedStartDate = startDate ? format(new Date(startDate), "yyyy-MM-dd'T'HH:mm") : undefined;
    const formattedExpirationDate = expirationDate ? format(new Date(expirationDate), "yyyy-MM-dd'T'HH:mm") : null;

    setSelectedAccount({
      id: accountId,
      name: `${firstName} ${lastName}`,
      startDate: formattedStartDate,
      expirationDate: formattedExpirationDate,
    });
    setDialogMode('edit');
    setDateDialogOpen(true);
  };

  // Handle date confirmation from dialog
  const handleDateConfirm = async (startDate: string, expirationDate: string | null) => {
    if (!selectedAccount) return;

    await upsertAccountRelation(communityId, selectedAccount.id, startDate, expirationDate);

    // Refresh both communities list and accounts table
    await Promise.all([loadCommunities(), loadAccounts(communityId)]);

    setSelectedAccount(null);
  };

  // Format date for display
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) {
      return '';
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  // Loading state
  if (isLoading && filteredAccounts.length === 0) {
    return (
      <div className="flex flex-col flex-1 gap-4">
        <Skeleton className="h-10 w-full" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-4">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">Failed to Load Accounts</h3>
          <p className="text-sm text-gray-600 mt-1">{error}</p>
        </div>
        <Button onClick={handleRetry} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
      </div>
    );
  }

  // Empty state - No accounts at all for the operator
  if (accounts.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">No Accounts Available</h3>
          <p className="text-sm text-gray-600 mt-1">
            No accounts found for this operator.
          </p>
        </div>
      </div>
    );
  }

  // Empty filter results - accounts exist but none match the filter
  if (filteredAccounts.length === 0 && accounts.length > 0 && !filters.search) {
    return (
      <div className="flex flex-col flex-1 gap-4">
        {/* Search Bar and Filter */}
        <div className="flex gap-4 items-center flex-shrink-0">
          <AccountsSearchBar
            ref={searchInputRef}
            value={filters.search}
            onChange={handleSearchChange}
            className="flex-1 max-w-md"
          />
          <RadioGroup
            value={filters.linkedFilter || 'Linked'}
            onValueChange={handleFilterChange}
            className="flex gap-4"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="All" id="all-accounts-empty" />
              <Label htmlFor="all-accounts-empty" className="cursor-pointer text-sm font-normal">All</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Linked" id="assigned-accounts-empty" />
              <Label htmlFor="assigned-accounts-empty" className="cursor-pointer text-sm font-normal">Assigned</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="flex flex-col items-center justify-center flex-1 gap-2">
          <p className="text-sm text-gray-600">
            {filters.linkedFilter === 'Linked' && 'No assigned accounts yet. Select "All Accounts" to view all available accounts.'}
          </p>
        </div>
      </div>
    );
  }

  // Empty search results
  if (filteredAccounts.length === 0 && filters.search) {
    return (
      <div className="flex flex-col flex-1 gap-4">
        <AccountsSearchBar
          ref={searchInputRef}
          value={filters.search}
          onChange={handleSearchChange}
          className="w-full max-w-md"
        />
        <div className="flex flex-col items-center justify-center flex-1 gap-2">
          <p className="text-sm text-gray-600">
            No accounts found matching "{filters.search}"
          </p>
        </div>
      </div>
    );
  }

  // Success state with data
  return (
    <div className="h-full flex flex-col">
      {/* Search Bar and Filter - Show search only when there are linked accounts or viewing all */}
      <div className="flex-shrink-0 flex gap-4 items-center p-4 border-b bg-white">
        {(filters.linkedFilter === 'All' || filteredAccounts.length > 0) && (
          <AccountsSearchBar
            ref={searchInputRef}
            value={filters.search}
            onChange={handleSearchChange}
            className="flex-1 max-w-md"
          />
        )}
        <RadioGroup
          value={filters.linkedFilter || 'Linked'}
          onValueChange={handleFilterChange}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="All" id="all-accounts" />
            <Label htmlFor="all-accounts" className="cursor-pointer text-sm font-normal">All</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Linked" id="assigned-accounts" />
            <Label htmlFor="assigned-accounts" className="cursor-pointer text-sm font-normal">Assigned</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Table with fixed header and scrollable body */}
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        <div className="relative w-full">
          <table className="w-full caption-bottom text-sm table-fixed">
            <thead className="sticky top-0 z-10 bg-white shadow-sm [&_tr]:border-b">
              <tr className="h-9 border-b transition-colors">
                <th className="h-9 px-0.5 text-center align-middle font-medium text-muted-foreground text-xs w-[35px] bg-white">
                  {/* Empty header for checkbox column */}
                </th>
                <th className="h-9 px-0.5 text-center align-middle font-medium text-muted-foreground text-xs w-[60px] bg-white">
                  ID
                </th>
                <th className="h-9 px-0.5 text-center align-middle font-medium text-muted-foreground text-xs w-[85px] bg-white">
                  First Name
                </th>
                <th className="h-9 px-0.5 text-center align-middle font-medium text-muted-foreground text-xs w-[85px] bg-white">
                  Last Name
                </th>
                <th className="h-9 px-0.5 text-center align-middle font-medium text-muted-foreground text-xs w-[110px] bg-white">
                  Start Date
                </th>
                <th className="h-9 px-0.5 text-center align-middle font-medium text-muted-foreground text-xs w-[110px] bg-white">
                  Expiration Date
                </th>
                <th className="h-9 px-0.5 text-center align-middle font-medium text-muted-foreground text-xs w-[60px] bg-white">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`[&_tr:last-child]:border-0 ${isLoading ? 'opacity-50' : ''}`}>
              {filteredAccounts.map((account) => (
                <tr key={account.accountId} className="h-9 max-h-9 border-b transition-colors hover:bg-muted/50">
                  <td className="h-9 max-h-9 px-0.5 align-middle text-center overflow-hidden">
                    <div className="flex items-center justify-center h-9">
                      <AssignedAccountCheckbox
                        isAssigned={account.isAssignedToCommunity}
                        disabled={false}
                        onChange={(checked) =>
                          handleCheckboxChange(
                            account.accountId,
                            account.firstName,
                            account.lastName,
                            checked
                          )
                        }
                      />
                    </div>
                  </td>
                  <td className="h-9 max-h-9 px-0.5 align-middle text-center text-xs overflow-hidden truncate">{account.accountId}</td>
                  <td className="h-9 max-h-9 px-0.5 align-middle text-center text-xs overflow-hidden truncate">{account.firstName}</td>
                  <td className="h-9 max-h-9 px-0.5 align-middle text-center text-xs overflow-hidden truncate">{account.lastName}</td>
                  <td className="h-9 max-h-9 px-0.5 align-middle text-center text-xs overflow-hidden truncate">
                    {account.isAssignedToCommunity ? formatDate(account.associatedDate) : ''}
                  </td>
                  <td className="h-9 max-h-9 px-0.5 align-middle text-center text-xs overflow-hidden truncate">
                    {account.isAssignedToCommunity ? formatDate(account.expirationDate) : ''}
                  </td>
                  <td className="h-9 max-h-9 px-0.5 align-middle text-center overflow-hidden">
                    {account.isAssignedToCommunity && (
                      <div className="flex items-center justify-center h-9">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() =>
                            handleEditAccount(
                              account.accountId,
                              account.firstName,
                              account.lastName,
                              account.associatedDate,
                              account.expirationDate
                            )
                          }
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results count - Fixed at bottom */}
      <div className="flex-shrink-0 flex items-center px-6 py-3 border-t bg-white">
        <div className="text-sm text-gray-700">
          Showing {filteredAccounts.length} account{filteredAccounts.length !== 1 ? 's' : ''}
          {' '}
          ({filteredAccounts.filter(a => a.isAssignedToCommunity).length} assigned)
        </div>
      </div>

      {/* Date Range Dialog */}
      {dateDialogOpen && selectedAccount && (
        <AccountDateRangeDialog
          isOpen={dateDialogOpen}
          accountName={selectedAccount.name}
          mode={dialogMode}
          initialStartDate={selectedAccount.startDate}
          initialExpirationDate={selectedAccount.expirationDate}
          onClose={() => {
            setDateDialogOpen(false);
            setSelectedAccount(null);
          }}
          onConfirm={handleDateConfirm}
        />
      )}
    </div>
  );
}
